import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { AppIcon } from "@/pages/Auth";
import { useMeta } from "@/lib/brand";
import { useInstall } from "@/lib/install";

const KEY = "mt_install_dismissed";

function dismissed() {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * A one-time "Install MetaTrack" card in the bottom corner (computers and Android),
 * or a short how-to sheet on iPhone/iPad. Install or Not now and it doesn't come back;
 * it's always available again from Your profile.
 */
export default function InstallPrompt() {
  const { user } = useAuth();
  const look = useMeta();
  const inst = useInstall();
  const [hidden, setHidden] = useState(dismissed);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 4000); // let the page settle first
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try {
      window.localStorage.setItem(KEY, "1");
    } catch {
      /* private browsing */
    }
    setHidden(true);
  };

  if (!user || hidden || !ready || inst.standalone) return null;
  const name = look.platform_name || "MetaTrack";

  if (inst.kind === "ios") {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:hidden" role="dialog" aria-label={`Install ${name}`}>
        <div className="panel shadow-2xl p-4 pb-5 animate-rise">
          <div className="flex items-center gap-3">
            <AppIcon look={look} size={44} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[14.5px]">Install {name}</div>
              <div className="text-[12.5px] text-muted2">Put it on your home screen</div>
            </div>
            <button onClick={close} aria-label="Not now" className="p-1.5 rounded-lg text-muted2 hover:bg-surface">
              <X className="w-4 h-4" />
            </button>
          </div>
          <ol className="mt-3 space-y-1.5 text-[13px] list-decimal pl-5">
            <li>
              Tap <Share className="inline w-4 h-4 -mt-0.5 text-[#0A84FF]" /> <b>Share</b> at the bottom of Safari
            </li>
            <li>
              Scroll and tap <b>Add to Home Screen</b>
            </li>
            <li>
              Tap <b>Add</b>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  if (!inst.canPrompt) return null;
  return (
    <div className="fixed z-50 bottom-4 right-4 left-4 sm:left-auto sm:w-[340px]" role="dialog" aria-label={`Install ${name}`}>
      <div className="panel shadow-2xl p-4 animate-rise">
        <div className="flex items-start gap-3">
          <AppIcon look={look} size={44} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[14.5px]">Install {name}</div>
            <div className="text-[12.5px] text-muted2 leading-snug">
              {inst.kind === "android" ? "Add it to your home screen and open it like any app." : "Open it from your desktop, in its own window."}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={close}>
            Not now
          </Button>
          <Button
            size="sm"
            className="bg-brand hover:bg-brand-hover"
            onClick={async () => {
              await inst.prompt();
              close();
            }}
          >
            Install
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The always-there version, for Your profile. */
export function InstallSection() {
  const look = useMeta();
  const inst = useInstall();
  const name = look.platform_name || "MetaTrack";
  let body;
  if (inst.standalone) body = <p className="text-[13.5px]">You're using the installed app ✓</p>;
  else if (inst.canPrompt)
    body = (
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[13.5px] flex-1 min-w-[200px]">{inst.kind === "android" ? "Add it to your home screen and open it like any app." : "Open it from your desktop or Start menu, in its own window."}</p>
        <Button className="bg-brand hover:bg-brand-hover" onClick={() => inst.prompt()}>
          Install {name}
        </Button>
      </div>
    );
  else if (inst.kind === "ios")
    body = (
      <ol className="space-y-1 text-[13.5px] list-decimal pl-5">
        <li>
          Open this page in <b>Safari</b>
        </li>
        <li>
          Tap <Share className="inline w-4 h-4 -mt-0.5 text-[#0A84FF]" /> <b>Share</b>, then <b>Add to Home Screen</b>
        </li>
        <li>
          Tap <b>Add</b>
        </li>
      </ol>
    );
  else
    body = (
      <p className="text-[13.5px]">
        {inst.installedBefore ? `${name} is installed on this device — open it from your apps.` : "Open this page in Chrome or Edge and use the Install button in the address bar, or the browser menu's “Install” option."}
      </p>
    );
  return (
    <section className="panel panel-pad space-y-3">
      <div className="flex items-center gap-3">
        <AppIcon look={look} size={40} />
        <div>
          <h2 className="font-display text-[18px] font-semibold">Install the app</h2>
          <p className="text-[12.5px] text-muted2">Use {name} like a normal app on this computer or phone. It updates itself.</p>
        </div>
      </div>
      {body}
    </section>
  );
}
